/**
 * Regelversion 12: Erfahrungsstufen statt Leicht, Normal, Schwer.
 *
 * Die Stufe legt Umfang und Härte fest:
 *  1 Neu im Bundestag      Kernspiel: Probleme, Gesetze, Koalition, Wahlen. Haushalt mit einem Klick,
 *                          Gesetzentwürfe mit empfohlener Einstellung, keine Affären, keine Inflation, keine Alterung.
 *  2 Erfahrene Abgeordnete wie bisher „Leicht“: dazu Haushalt, Kommunikation, Meinungsthemen, Vorwahlkampf, Spenden.
 *  3 Regierungsprofi       wie bisher „Normal“: Gerichte, Verbände, Flügel, Verwaltung, Wirtschaftsordnung. In der Opposition
 *                          kann die Computerregierung die Demokratie angreifen (auf Stufe 1 und 2 nicht).
 *  4 Machtpolitik          wie bisher „Schwer“, dazu die Machtfragen: selbst in die Gewaltenteilung eingreifen.
 * Nach jeder gewonnenen Wahl kann man eine Stufe aufsteigen. Die einzelnen Einflüsse lassen sich weiter schalten:
 * Wer etwas abschaltet, das die Stufe enthält, spielt „angepasst“ und bekommt den Faktor der Stufe darunter.
 * Ältere Partien behalten Leicht, Normal und Schwer (angezeigt als Stufe 2, 3, 4).
 */
import { S, log } from './state';
import { since } from './version';
import { PRESETS } from '../content/settings';
import { hardPenalty } from './hard';

export const lvOn = (): boolean => since(12) && !S.mp;
export type Level = { l: number; diff: 'leicht' | 'normal' | 'schwer'; n: string; tag: string; pts: string[] };
export const LEVELS: Level[] = [
  { l: 1, diff: 'leicht', n: 'Neu im Bundestag', tag: 'Zum Kennenlernen', pts: [
    'Das Kernspiel: Probleme lösen, Gesetze beschließen, Koalition bilden, Wahlen gewinnen',
    'Gesetzentwürfe starten mit einer empfohlenen Einstellung',
    'Den Haushalt beschließt ihr mit einem Klick',
    'Seltene Krisen, keine Affären, keine Inflation',
    'Nach jeder gewonnenen Wahl könnt ihr aufsteigen'] },
  { l: 2, diff: 'leicht', n: 'Erfahrene Abgeordnete', tag: 'Mehr Werkzeuge', pts: [
    'Haushalt selbst aufstellen, Kommunikation, Meinungsthemen',
    'Vorwahlkampf mit TV-Duellen, Spenden, Affären, Inflation',
    'Keine Klagen in Karlsruhe, keine Verbände, keine Parteiflügel',
    'Die Wähler werden euch langsamer müde'] },
  { l: 3, diff: 'normal', n: 'Regierungsprofi', tag: 'Das ganze Spiel', pts: [
    'Alle Einflüsse in mittlerer Stärke: Gerichte, Verbände, Parteiflügel, Verwaltung',
    'Ab der zweiten Legislatur die Wirtschaftsordnung',
    'Die Opposition klagt, Verbände protestieren oder loben',
    'In der Opposition verteidigt ihr notfalls die Demokratie'] },
  { l: 4, diff: 'schwer', n: 'Machtpolitik', tag: 'Für Profis', pts: [
    'Machtfragen: Mit eigener Mehrheit könnt ihr in Gerichte, Rundfunk und Wahlrecht eingreifen, mit allen Folgen',
    'Alle Einflüsse wirken stärker und häufiger',
    'Große Steuerreform statt einfacher Steuerregler, Verwaltung im Detail',
    'Wähler verzeihen bei Wiederwahlen weniger'] },
];
/** Schalter je Stufe (Stufe 2–4 wie die bisherigen Voreinstellungen). */
export const LEVEL_CFG: Record<number, Record<string, any>> = {
  1: { ...PRESETS.leicht, crisis: true, infl: false, demo: false, scandal: false, events: true, adm: 0 },
  2: { ...PRESETS.leicht }, 3: { ...PRESETS.normal }, 4: { ...PRESETS.schwer },
};
/** Ab welcher Stufe ein Werkzeug da ist. */
export const LV_MIN: Record<string, number> = { presse: 2, agenda: 2, pre: 2, don: 2, budget: 2, verwaltung: 3, ordnung: 3, macht: 4 };
/** Faktor für die Bestenliste je Stufe. */
export const LV_FACTOR = [.6, .8, 1, 1.25];

const A = (s: any = S) => s as any;
/** Aktuelle Stufe (ältere Partien: aus der Schwierigkeit abgeleitet). */
export function lvlOf(s: any = S): number { const c = s?.cfg || {}; return c.lvl ?? (c.diff === 'leicht' ? 2 : c.diff === 'schwer' ? 4 : 3); }
export const lvl = (): number => lvlOf(S);
export const levelName = (l: number) => LEVELS[l - 1]?.n ?? '';
/** Hat die aktuelle Stufe dieses Werkzeug? Vor Version 12 immer. */
export const lvlHas = (id: string): boolean => !lvOn() || lvl() >= (LV_MIN[id] ?? 1);

/** Stufe setzen (Programm, Einstellungen, Aufstieg). */
export function setLevel(l: number) {
  if (!since(12) || S.mp) return;
  l = Math.max(1, Math.min(4, Math.round(Number(l)) || 1));
  const L = LEVELS[l - 1], q = A().cfg?.qr === false ? { qr: false } : {};
  S.cfg = { diff: L.diff, ...LEVEL_CFG[l], lvl: l, ...q } as any;
  A().lvMin = Math.min(A().lvMin ?? l, l); A().lvStart ??= l;
  if (S.phase === 'program') { A().lvMin = l; A().lvStart = l; }
}
/** Nach gewonnener Wahl eine Stufe aufsteigen. Aufruf aus nextLeg. */
export function lvUp() {
  const l = lvl(); if (!lvOn() || l >= 4) return;
  setLevel(l + 1);
  if (l + 1 === 4) { const pen = hardPenalty(S.final); if (pen > 0) S.elec = Math.max(4, S.elec - pen); }
  log(`Ihr steigt auf: Ab jetzt spielt ihr als „${levelName(l + 1)}“.`, 'good');
}
/** Wer einen Einfluss abschaltet, den die Stufe enthält, spielt angepasst. Aufruf aus setCfg. */
export function lvToggle(k: string, onNow: boolean) {
  if (!lvOn() || onNow) return;
  const def = LEVEL_CFG[lvl()]?.[k];
  if (def === true) { A().adjLeg = true; A().adjAny = true; }
}
/** Zu Beginn jeder Legislatur. */
export function lvLegStart() {
  if (!lvOn()) return; const D = LEVEL_CFG[lvl()] || {};
  A().lvMin = lvl(); A().adjLeg = Object.keys(D).some((k) => D[k] === true && !(S.cfg as any)[k]);
}
/** Merkmale für den Eintrag in der Karriere (Stufe und angepasst). */
export function lvTag(): { lv?: number; adj?: boolean } { return lvOn() ? { lv: A().lvMin ?? lvl(), adj: !!A().adjLeg } : {}; }
/** Faktor einer Legislatur. */
export function legFactor(c: any): number {
  if (!c?.lv) return 1;
  const l = c.adj ? c.lv - 1 : c.lv;
  return l >= 1 ? LV_FACTOR[l - 1] : .5;
}
/** Bezeichnung für die Bestenliste (Spalte „difficulty“). */
export function diffKey(s: any = S): string {
  const C = s?.career || [], last = C[C.length - 1];
  if (last?.lv) return `stufe-${last.lv}${C.some((c: any) => c.adj) ? '-angepasst' : ''}`;
  if (s?.cfg?.lvl && s?.ver >= 12) return `stufe-${s.cfg.lvl}${A(s).adjAny ? '-angepasst' : ''}`;
  return s?.cfg?.diff ?? 'normal';
}
/** Anzeige einer Bestenlisten-Kennung. */
export function diffLabel(d: string): string {
  const m = /^stufe-(\d)(-angepasst)?$/.exec(d || '');
  if (m) return `${levelName(+m[1])}${m[2] ? ' (angepasst)' : ''}`;
  return ({ leicht: 'Erfahrene Abgeordnete', normal: 'Regierungsprofi', schwer: 'Machtpolitik' } as Record<string, string>)[d] || d;
}
